import {
	getAuth,
	signInWithPopup,
	GoogleAuthProvider,
	FacebookAuthProvider,
	TwitterAuthProvider,
	OAuthProvider,
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import toast from 'react-hot-toast';
import { t } from 'i18next';

const firebaseConfig = {
	apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
	authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
	projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
	storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
	appId: import.meta.env.VITE_FIREBASE_APP_ID,
	measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);

// ----------------------------------Google Login--------------------------------------------

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
	prompt: 'select_account',
});

export const auth = getAuth();
export const signInWithGooglePopup = () =>
	signInWithPopup(auth, googleProvider).catch((error) => handleAuthError(error, 'Google'));

// ----------------------------------Facebook Login--------------------------------------------

const fbProvider = new FacebookAuthProvider();
fbProvider.setCustomParameters({
	display: 'popup',
});

export const signInWithFacebookPopup = () =>
	signInWithPopup(auth, fbProvider).catch((error) => handleAuthError(error, 'Facebook'));

// ----------------------- Twitter Login ----------------------------

const twitterProvider = new TwitterAuthProvider();
export const signInWithXPopup = () =>
	signInWithPopup(auth, twitterProvider).catch((error) => handleAuthError(error, 'Twitter'));

const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.setCustomParameters({
	locale: 'en',
});

export const signInWithApplePopup = () =>
	signInWithPopup(auth, appleProvider).catch((error) => handleAuthError(error, 'Apple'));

// ----------------------- Error Handling ----------------------------

const handleAuthError = (error: any, platform: string) => {
	let errorMessage = 'An unexpected error occurred while signing in.';
	if (error.code) {
		switch (error.code) {
			case 'auth/invalid-email':
				errorMessage = t('Auth.invalidEmail');
				break;
			case 'auth/email-already-in-use':
				errorMessage = t('Auth.emailInUse');
				break;
			case 'auth/wrong-password':
				errorMessage = t('Auth.wrongPassword');
				break;
			case 'auth/weak-password':
				errorMessage = t('Auth.weakPassword');
				break;
			case 'auth/user-disabled':
				errorMessage = t('Auth.userDisabled');
				break;
			case 'auth/user-not-found':
				errorMessage = t('Auth.userNotFound');
				break;
			case 'auth/account-exists-with-different-credential':
				errorMessage = t('Auth.accountExistsWithDifferentCredential', { platform });
				break;
			case 'auth/operation-not-allowed':
				errorMessage = t('Auth.operationNotAllowed');
				break;
			case 'auth/credential-already-in-use':
				errorMessage = t('Auth.credentialInUse');
				break;
			case 'auth/popup-closed-by-user':
				errorMessage = t('Auth.popupClosed');
				break;
			case 'auth/cancelled-popup-request':
				errorMessage = t('Auth.popupCancelled');
				break;
			case 'auth/popup-blocked':
				errorMessage = t('Auth.popupBlocked');
				break;
			case 'auth/network-request-failed':
				errorMessage = t('Auth.networkError');
				break;
			case 'auth/quota-exceeded':
				errorMessage = t('Auth.quotaExceeded');
				break;
			case 'auth/missing-phone-number':
				errorMessage = t('Auth.missingPhoneNumber');
				break;
			case 'auth/invalid-verification-code':
				errorMessage = t('Auth.invalidVerificationCode');
				break;
			case 'auth/missing-verification-code':
				errorMessage = t('Auth.missingVerificationCode');
				break;
			case 'auth/timeout':
				errorMessage = t('Auth.timeout');
				break;
			case 'auth/too-many-requests':
				errorMessage = t('Auth.tooManyRequests');
				break;
			case 'auth/internal-error':
				errorMessage = t('Auth.internalError');
				break;
			default:
				errorMessage =
					error?.message || t('Auth.defaultError', { platform, message: error.message });
		}
	}

	toast.error(errorMessage);
	console.error(`${platform} Auth Error:`, error);
};
